import {
  CreditCard,
  FileText,
  BookOpen,
  UserCheck,
  Landmark,
  Users,
  Map,
  GraduationCap,
  FileSignature,
  Briefcase,
  ShieldCheck,
  Train,
  Plane,
} from "lucide-react";

export const serviceCategories = [
  "All",
  "Document Services",
  "Government Services",
  "Financial & Business",
  "Travel Services",
];

export const servicesData = [
  {
    id: "srv-01",
    name: "Aadhar Card Services",
    category: "Document Services",
    icon: CreditCard,
    description:
      "Complete Aadhar related services including new enrollment guidance, updates, and PVC card ordering.",
    included: [
      "Name, Address & DOB correction",
      "Mobile number linking assistance",
      "Biometric update guidance",
      "PVC card ordering",
      "Aadhar download and print",
    ],
    howToApply:
      "Visit our center with your original Aadhar card and valid proof of identity/address for the required updates.",
    documents: [
      "Original Aadhar Card",
      "Valid ID Proof (PAN, Voter ID, etc.)",
      "Valid Address Proof (Ration Card, Bank Passbook, etc.)",
      "Registered Mobile Number",
    ],
    processingTime: "3-7 Working Days (for updates to reflect)",
    color: "from-blue-500 to-indigo-500",
  },
  {
    id: "srv-02",
    name: "PAN Card Services",
    category: "Document Services",
    icon: FileText,
    description:
      "Apply for a new PAN card or make corrections to your existing PAN card quickly and easily.",
    included: [
      "New PAN Card application",
      "PAN Card correction/updates",
      "Reprint of lost PAN Card",
      "Aadhar-PAN linking",
    ],
    howToApply:
      "Provide your Aadhar details and passport size photographs. We will fill and submit the form online.",
    documents: [
      "Aadhar Card (with full DOB)",
      "2 Passport Size Photographs",
      "Signature on white paper",
    ],
    processingTime: "7-15 Working Days (Physical card delivery)",
    color: "from-orange-500 to-red-500",
  },
  {
    id: "srv-03",
    name: "Passport Application",
    category: "Document Services",
    icon: BookOpen,
    description:
      "Assistance with online passport application, appointment booking, and document verification guidance.",
    included: [
      "Fresh Passport Application",
      "Passport Renewal/Re-issue",
      "Tatkaal Passport Application",
      "Appointment scheduling",
    ],
    howToApply:
      "Bring your original documents. We will complete the online application and book your slot at the nearest PSK.",
    documents: [
      "Aadhar Card",
      "10th/12th Marksheet (for Non-ECR)",
      "Birth Certificate",
      "Address Proof (Voter ID, Bank Passbook)",
    ],
    processingTime: "1-2 Days for application; PSK processing varies",
    color: "from-sky-500 to-blue-600",
  },
  {
    id: "srv-04",
    name: "Voter ID Services",
    category: "Document Services",
    icon: UserCheck,
    description:
      "Register as a new voter, update your details, or download your digital Voter ID (e-EPIC).",
    included: [
      "New Voter Registration (Form 6)",
      "Correction of Entries (Form 8)",
      "Aadhar linking with Voter ID",
      "e-EPIC Download & Print",
    ],
    howToApply:
      "Provide your age and address proofs. We will submit the application through the NVSP portal.",
    documents: [
      "Passport Size Photo",
      "Age Proof (Birth Cert, 10th Marksheet, PAN)",
      "Address Proof (Aadhar, Ration Card)",
    ],
    processingTime: "15-30 Days (Subject to ERO approval)",
    color: "from-emerald-500 to-green-600",
  },
  {
    id: "srv-05",
    name: "Income Certificate",
    category: "Government Services",
    icon: Landmark,
    description:
      "Apply for an Income Certificate required for scholarships, fee concessions, and government schemes.",
    included: [
      "Online application submission",
      "Document scanning and upload",
      "Application status tracking",
      "Final certificate download and print",
    ],
    howToApply:
      "Submit the required documents at our center. We will apply through the e-Sevai portal.",
    documents: [
      "Ration Card / Smart Card",
      "Aadhar Card",
      "Recent Salary Slip / Income Affidavit",
      "Passport Size Photo",
    ],
    processingTime: "7-15 Working Days",
    color: "from-amber-500 to-yellow-600",
  },
  {
    id: "srv-06",
    name: "Community Certificate",
    category: "Government Services",
    icon: Users,
    description:
      "Obtain your Community/Caste Certificate for educational admissions and employment reservations.",
    included: [
      "e-Sevai application processing",
      "Family tree documentation help",
      "Status tracking",
      "Digital certificate delivery",
    ],
    howToApply:
      "Bring your parents'/siblings' community certificates as reference along with your ID proofs.",
    documents: [
      "Applicant's Aadhar & Photo",
      "Father/Mother/Sibling Community Certificate",
      "School Transfer Certificate (TC)",
      "Ration Card",
    ],
    processingTime: "10-20 Working Days",
    color: "from-purple-500 to-fuchsia-600",
  },
  {
    id: "srv-07",
    name: "Nativity Certificate",
    category: "Government Services",
    icon: Map,
    description:
      "Apply for a Nativity/Residence Certificate to prove your continuous residence in the state.",
    included: [
      "Application drafting",
      "Document compilation",
      "Online submission",
      "Certificate retrieval",
    ],
    howToApply:
      "Provide proof of continuous residence for the past 5 years along with standard ID proofs.",
    documents: [
      "Aadhar Card",
      "Ration Card",
      "Birth Certificate / School TC",
      "Proof of residence for 5 years",
    ],
    processingTime: "7-15 Working Days",
    color: "from-teal-500 to-emerald-600",
  },
  {
    id: "srv-08",
    name: "First Graduate Certificate",
    category: "Government Services",
    icon: GraduationCap,
    description:
      "Get a First Graduate Certificate to avail tuition fee concessions for higher education.",
    included: [
      "Eligibility verification",
      "Application submission",
      "Affidavit preparation assistance",
      "Status monitoring",
    ],
    howToApply:
      "Ensure no other family member has claimed this benefit. Submit educational details of all family members.",
    documents: [
      "Applicant's Aadhar & Photo",
      "Applicant's 10th/12th Marksheet & TC",
      "Parents' Educational Proofs (if any)",
      "Ration Card showing all family members",
    ],
    processingTime: "10-15 Working Days",
    color: "from-cyan-500 to-blue-500",
  },
  {
    id: "srv-09",
    name: "Patta / Chitta Download",
    category: "Government Services",
    icon: FileSignature,
    description:
      "View and download your land records (Patta, Chitta, A-Register) from the Tamil Nadu land records portal.",
    included: [
      "Patta/Chitta view and print",
      "A-Register extract",
      "FMB Sketch download",
      "Application for Patta transfer (Guidance)",
    ],
    howToApply:
      "Provide your District, Taluk, Village, and Survey Number/Sub-division Number.",
    documents: [
      "Survey Number / Patta Number",
      "Previous land documents (for reference)",
    ],
    processingTime: "Instant (for download)",
    color: "from-stone-500 to-slate-600",
  },
  {
    id: "srv-10",
    name: "PF Claim & Withdrawal",
    category: "Financial & Business",
    icon: Briefcase,
    description:
      "Expert assistance for Provident Fund (EPF) withdrawals, transfers, and KYC updates.",
    included: [
      "UAN Activation & Password Reset",
      "KYC Update (Bank, PAN, Aadhar)",
      "Full/Partial Withdrawal Claims (Form 19, 10C, 31)",
      "PF Transfer from old to new company",
    ],
    howToApply:
      "Ensure your UAN is linked with your Aadhar and mobile number. Bring your bank details.",
    documents: [
      "UAN Number & Password",
      "Aadhar Card (Linked to UAN mobile)",
      "PAN Card",
      "Bank Passbook / Cancelled Cheque",
    ],
    processingTime: "7-20 Days (Subject to EPFO processing)",
    color: "from-rose-500 to-pink-600",
  },
  {
    id: "srv-11",
    name: "FSSAI Registration",
    category: "Financial & Business",
    icon: ShieldCheck,
    description:
      "Get your Food Safety and Standards Authority of India (FSSAI) basic registration for small food businesses.",
    included: [
      "FoSCoS portal registration",
      "Application filing",
      "Fee payment processing",
      "Certificate download",
    ],
    howToApply:
      "Provide details about your food business, premises, and identity proofs.",
    documents: [
      "Passport Size Photo",
      "Aadhar Card / PAN Card",
      "Proof of Business Premises (Rent agreement/EB Bill)",
      "Declaration form",
    ],
    processingTime: "7-10 Working Days",
    color: "from-lime-500 to-green-500",
  },
  {
    id: "srv-12",
    name: "MSME / Udyam Registration",
    category: "Financial & Business",
    icon: Landmark,
    description:
      "Register your micro, small, or medium enterprise to avail government subsidies and bank loan benefits.",
    included: [
      "Udyam portal registration",
      "NIC code selection",
      "Application submission",
      "Udyam Certificate generation",
    ],
    howToApply:
      "Provide your Aadhar number linked with your mobile and basic business details.",
    documents: [
      "Aadhar Card of Proprietor/Director",
      "PAN Card",
      "Bank Account Details",
      "Business Address Details",
    ],
    processingTime: "1-2 Working Days",
    color: "from-indigo-500 to-violet-600",
  },
  {
    id: "srv-13",
    name: "Train Ticket Booking",
    category: "Travel Services",
    icon: Train,
    description:
      "Authorized IRCTC ticket booking for all classes and routes across India.",
    included: [
      "General & Tatkal booking",
      "Waitlist confirmation assistance",
      "Senior citizen & quota bookings",
      "Instant cancellation & refunds",
    ],
    howToApply:
      "Provide passenger details, travel dates, and destination. We will find the best available trains.",
    documents: [
      "Passenger Names & Ages",
      "Valid ID Proof (for travel)",
      "Travel Dates & Destinations",
    ],
    processingTime: "Instant",
    color: "from-blue-600 to-cyan-600",
  },
  {
    id: "srv-14",
    name: "Flight & Bus Booking",
    category: "Travel Services",
    icon: Plane,
    description:
      "Book comfortable bus journeys and domestic/international flights at competitive prices.",
    included: [
      "Flight ticket booking & comparison",
      "Bus ticket booking (AC/Non-AC/Sleeper)",
      "Web check-in assistance",
      "Boarding pass printing",
    ],
    howToApply:
      "Share your travel itinerary. We will provide multiple options and book your preferred choice.",
    documents: [
      "Passenger Names (as per ID/Passport)",
      "Valid ID Proof / Passport (for flights)",
      "Travel Dates",
    ],
    processingTime: "Instant",
    color: "from-sky-400 to-blue-500",
  },
];
